import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setToken } from "@/lib/api-core";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Shield, Loader2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { refresh } = useAuth() || {};
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await api.post("/admin/login", { username, password });
      setToken(r.data.session_token);
      await refresh?.();
      toast.success("Welcome back, admin");
      navigate("/admin", { replace: true });
    } catch (e2) {
      const msg = e2?.response?.data?.detail || "Login failed";
      setErr(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage:
            "radial-gradient(900px 600px at 12% -8%, rgba(245, 166, 35, 0.10), transparent 60%), " +
            "radial-gradient(700px 500px at 95% 0%, rgba(220, 38, 38, 0.06), transparent 60%)",
        }}
      />
      <Link to="/" className="absolute top-6 left-6 text-zinc-400 hover:text-amber inline-flex items-center gap-1.5 font-heading text-sm" data-testid="admin-login-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </Link>

      <div className="w-full max-w-md" data-testid="admin-login-page">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-md bg-amber/15 border border-amber/30 mb-4">
            <Shield className="h-7 w-7 text-amber" />
          </div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-amber font-heading mb-1">MovioHub</div>
          <h1 className="font-display text-4xl text-white">Admin Console</h1>
          <p className="text-zinc-500 font-body text-sm mt-2">Privileged access. All actions are logged.</p>
        </div>

        <form onSubmit={submit} className="glass-strong rounded-md p-6 space-y-4 border border-white/10">
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-heading">Username</label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
              className="mt-1 w-full bg-black/30 border border-white/10 rounded-md px-3 py-2.5 text-zinc-100 outline-none focus:border-amber/50 font-body"
              data-testid="admin-username-input"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-heading">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              className="mt-1 w-full bg-black/30 border border-white/10 rounded-md px-3 py-2.5 text-zinc-100 outline-none focus:border-amber/50 font-body"
              data-testid="admin-password-input"
            />
          </div>

          {err && (
            <div className="px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-300 text-sm font-heading" data-testid="admin-login-error">
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            className="w-full bg-amber hover:bg-amber-hover text-obsidian font-heading shadow-[0_10px_40px_-12px_rgba(245,166,35,0.6)]"
            data-testid="admin-login-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />}
            Sign in to admin
          </Button>
        </form>

        <p className="text-center text-xs text-zinc-600 mt-6 font-heading">
          Are you a user? <Link to="/" className="text-amber hover:underline">Go to MovioHub home</Link>
        </p>
      </div>
    </div>
  );
}
